import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import {
  getCasesAsync,
  loadMoreCasesAsync,
  updateCaseAsync,
} from "../../../slices/case";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ChecklistCompactPageHeader from "../../../components/dashboard/quality/checklists/ChecklistCompactPageHeader";
import { PMDialog } from "../../../components/utils/PMDialog";
import { fetchTagDefinitions } from "../../../api/tagDefinitions";
import { debounce } from "lodash";
import { toast } from "react-hot-toast";
import { priorityOptions } from "../../../components/dashboard/quality/deviations/utils";
import {
  CasesTable,
  getCaseColumns,
  CASES_TABLE_FIELDS,
} from "../../../components/dashboard/cases/cases-table";
import { CaseDetailDrawer } from "../../../components/dashboard/cases/CaseDetailDrawer";
import { isUnreadEmailTag } from "../../../components/dashboard/cases/unreadInboundMail";
import { getUsersAsync } from "../../../slices/user";
import { fetchCaseTypes } from "../../../api/case/type";
import { searchFieldsByName, updateUrlWithException } from "../../../components/Utils";
import CaseCreateOnboardingDialog from "../../../components/dashboard/cases/guides/CaseCreateOnboardingDialog";
import { BulkBar } from "../../../components/utils/BulkBar";
import { TableToolbar } from "../../../components/utils/TableToolbar";
import { useTableView } from "../../../components/utils/PMTableView/useTableView";
import { RightDrawer } from "../../../components/utils/PMTableView/RightDrawer";
import { useUserHasAccess } from "../../../routes/roleOnly";

/** URL value when no status restriction (all statuses). */
const CASE_STATUS_URL_ALL = "all";
const DEFAULT_CASE_STATUSES = ["NEW", "PROCESSING", "FINISHED"];
const FETCH_DEBOUNCE_MS = 400;
const CASE_SECONDARY_FILTER_NAMES = [
  "user_assigned_id",
  "case_type",
  "priority",
  "tag_ids",
];

function parseCasesUrlMeta(searchParams) {
  const rawStatus = searchParams.get("status");
  let statuses;
  if (rawStatus === null || rawStatus === undefined) {
    statuses = [...DEFAULT_CASE_STATUSES];
  } else if (rawStatus === CASE_STATUS_URL_ALL) {
    statuses = [];
  } else {
    statuses = rawStatus.split(",").map((s) => s.trim()).filter(Boolean);
  }
  const overdueRaw = searchParams.get("only_overdue");
  const onlyOverdue =
    overdueRaw === null || overdueRaw === undefined
      ? true
      : overdueRaw === "1" || overdueRaw === "true";
  return { statuses, onlyOverdue };
}

function buildBackendFiltersFromModuleFilter(filters) {
  const backendFilters = [];
  const searchQuery = filters.query || "";
  const hasSearchQuery = searchQuery.trim().length > 0;

  if (filters.statuses && filters.statuses.length > 0 && !hasSearchQuery) {
    backendFilters.push({
      name: "status",
      value: JSON.stringify(filters.statuses),
    });
  }

  if (filters.users && filters.users.length > 0) {
    backendFilters.push({
      name: "user_assigned_id",
      value: JSON.stringify(filters.users),
    });
  }

  if (filters.caseTypes && filters.caseTypes.length > 0) {
    backendFilters.push({
      name: "case_type",
      value: JSON.stringify(filters.caseTypes),
    });
  }

  if (filters.priorities && filters.priorities.length > 0) {
    backendFilters.push({
      name: "priority",
      value: JSON.stringify(filters.priorities),
    });
  }

  if (filters.tags && filters.tags.length > 0) {
    backendFilters.push({
      name: "tag_ids",
      value: JSON.stringify(filters.tags.map((id) => parseInt(id, 10))),
    });
  }

  if (filters.onlyOverdue && !hasSearchQuery) {
    backendFilters.push({
      name: "view",
      value: JSON.stringify(["ONLY_OVERDUE"]),
    });
  }

  return { backendFilters, searchQuery };
}

function deriveMetaFromBackendFilters(backendFilters, searchParams) {
  const meta = parseCasesUrlMeta(searchParams);

  const statusFilter = (backendFilters || []).find((f) => f.name === "status");
  if (statusFilter?.value) {
    try {
      meta.statuses = JSON.parse(statusFilter.value);
    } catch {
      // keep URL-derived statuses
    }
  }

  const viewFilter = (backendFilters || []).find((f) => f.name === "view");
  if (viewFilter?.value) {
    try {
      const views = JSON.parse(viewFilter.value);
      meta.onlyOverdue = Array.isArray(views) && views.includes("ONLY_OVERDUE");
    } catch {
      // keep URL-derived onlyOverdue
    }
  }

  return meta;
}

function parseFiltersFromUrl(searchParams) {
  const filtersParam = searchParams.get("filters");
  if (!filtersParam) return null;
  try {
    const parsed = JSON.parse(filtersParam);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    console.error("Error parsing case filters from URL:", e);
    return null;
  }
}

function createInitialController(searchString) {
  const sp = new URLSearchParams(searchString);
  const query = sp.get("query") || "";
  const filtersFromUrl = parseFiltersFromUrl(sp);

  let backendFilters;
  let meta;

  if (filtersFromUrl) {
    backendFilters = filtersFromUrl;
    meta = deriveMetaFromBackendFilters(backendFilters, sp);
  } else {
    meta = parseCasesUrlMeta(sp);
    ({ backendFilters } = buildBackendFiltersFromModuleFilter({
      query,
      statuses: meta.statuses,
      onlyOverdue: meta.onlyOverdue,
      users: [],
      caseTypes: [],
      priorities: [],
      tags: [],
    }));
  }

  return {
    filters: backendFilters,
    meta,
    page: sp.get("page") || 1,
    per_page: sp.get("per_page") || 25,
    query,
    sort: sp.get("sort") || "desc",
    sortBy: sp.get("sortBy") || "created_at",
    view: sp.get("view") || "all",
    fields: CASES_TABLE_FIELDS,
  };
}

const caseCustomerName = (item) =>
  item?.customer?.id
    ? item?.customer?.displayTitle
    : item?.fields
    ? searchFieldsByName(item, "name")?.value
    : null;

export const CasesList = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canCreateCase = useUserHasAccess({ role: "cases_create" });
  const canEditCase = useUserHasAccess({ role: "cases_edit" });

  const caseState = useSelector((store) => store.cases);
  const userState = useSelector((store) => store.users);

  const { isLoading } = caseState;
  const [caseTypes, setCaseTypes] = useState([]);
  const [caseTags, setCaseTags] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [drawerCase, setDrawerCase] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const skipFetchRef = useRef(false);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const debouncedFetchRef = useRef(null);
  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce((ctrl) => {
      if (skipFetchRef.current) {
        skipFetchRef.current = false;
        return;
      }
      dispatchRef.current(getCasesAsync(ctrl));
    }, FETCH_DEBOUNCE_MS);
  }

  const [controller, setController] = useState(() =>
    createInitialController(
      typeof window !== "undefined"
        ? window.location.search.substring(1)
        : searchParams.toString()
    )
  );

  const userOptions = useMemo(
    () =>
      (userState.data || []).map((user) => ({
        value: user.id.toString(),
        label: user.displayTitle || user.name,
      })),
    [userState.data]
  );

  // Persist an inline change. The slice swaps the row in place, so no full
  // refetch is needed; we also sync the open drawer copy.
  const handleCaseUpdate = useCallback(
    async (caseItem, patch) => {
      if (!canEditCase) {
        toast.error(t("Could not save changes"));
        return null;
      }
      try {
        const updated = await dispatch(
          updateCaseAsync({ ...caseItem, ...patch })
        ).unwrap();
        if (updated?.id) {
          toast.success(t("Saved"));
          setDrawerCase((prev) =>
            prev && prev.id === updated.id ? { ...prev, ...updated } : prev
          );
          return updated;
        }
        toast.error(t("Could not save changes"));
        return null;
      } catch (err) {
        toast.error(t("Could not save changes"));
        throw err;
      }
    },
    [canEditCase, dispatch, t]
  );

  // ── Table-view state (columns, grouping, density, saved views) ───────────
  const columns = useMemo(
    () => getCaseColumns(t, { onUpdate: handleCaseUpdate, userOptions }),
    [t, handleCaseUpdate, userOptions]
  );
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "cases",
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      meta: controller.meta,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        meta: snap.meta || c.meta,
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = {
    field: controller.sortBy,
    dir: String(controller.sort).toLowerCase() === "asc" ? "asc" : "desc",
  };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort:
        c.sortBy === field && String(c.sort).toLowerCase() === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // Persist controller + full backend filters to the URL (shareable links)
  useEffect(() => {
    const m = controller.meta;
    const statusVal =
      m?.statuses?.length === 0 ? CASE_STATUS_URL_ALL : m?.statuses?.join(",");

    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        status: statusVal,
        only_overdue: m?.onlyOverdue ? "1" : "0",
        filters:
          controller.filters && controller.filters.length > 0
            ? JSON.stringify(controller.filters)
            : null,
      },
      "filters"
    );
  }, [controller]);

  const handleFiltersChange = (filters) => {
    skipFetchRef.current = false;
    const { backendFilters, searchQuery } = buildBackendFiltersFromModuleFilter({
      query: filters.query || "",
      statuses: filters.statuses || [],
      onlyOverdue: !!filters.onlyOverdue,
      users: filters.users || [],
      caseTypes: filters.caseTypes || [],
      priorities: filters.priorities || [],
      tags: filters.tags || [],
    });

    setController((prev) => ({
      ...prev,
      page: 1,
      query: searchQuery,
      filters: backendFilters,
      meta: {
        statuses: filters.statuses || [],
        onlyOverdue: !!filters.onlyOverdue,
      },
    }));
  };

  // Infinite scroll: load the next page and append results
  const handleLoadMore = () => {
    const nextPage = Number(controller.page) + 1;
    skipFetchRef.current = true;
    dispatch(loadMoreCasesAsync({ ...controller, page: nextPage }));
    setController((prev) => ({ ...prev, page: nextPage }));
  };

  useEffect(() => {
    return () => debouncedFetchRef.current?.cancel();
  }, []);

  useEffect(() => {
    debouncedFetchRef.current(controller);
  }, [controller]);

  useEffect(() => {
    dispatch(
      getUsersAsync({
        filters: [{ name: "role", value: "case" }],
        page: 1,
        per_page: 100,
        query: "",
        sort: "asc",
        sortBy: "name",
        view: "all",
        fields: "id,name,displayTitle",
      })
    );

    fetchCaseTypes()
      .then((response) => {
        const data = response?.data?.data || response?.data || [];
        setCaseTypes(data);
      })
      .catch((error) => {
        console.error("Error fetching case types:", error);
      });

    fetchTagDefinitions("case")
      .then((data) => setCaseTags(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error("Error fetching case tags:", error);
        setCaseTags([]);
      });
  }, [dispatch]);

  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query || "",
      statuses: controller.meta?.statuses ?? [],
      users: [],
      caseTypes: [],
      priorities: [],
      tags: [],
      onlyOverdue: controller.meta?.onlyOverdue ?? false,
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        if (filter.name === "status" || filter.name === "view") {
          return;
        }
        try {
          const values = JSON.parse(filter.value);
          switch (filter.name) {
            case "user_assigned_id":
              initialFilters.users = Array.isArray(values) ? values : [values];
              break;
            case "case_type":
              initialFilters.caseTypes = Array.isArray(values) ? values : [values];
              break;
            case "priority":
              initialFilters.priorities = Array.isArray(values) ? values : [values];
              break;
            case "tag_ids":
              initialFilters.tags = (Array.isArray(values) ? values : [values]).map(String);
              break;
            default:
              break;
          }
        } catch (e) {
          switch (filter.name) {
            case "user_assigned_id":
              initialFilters.users = [filter.value];
              break;
            case "case_type":
              initialFilters.caseTypes = [filter.value];
              break;
            case "priority":
              initialFilters.priorities = [filter.value];
              break;
            case "tag_ids":
              initialFilters.tags = [String(filter.value)];
              break;
            default:
              break;
          }
        }
      });
    }

    return initialFilters;
  };

  const handleQueryChange = (newQuery) => {
    handleFiltersChange({
      ...getInitialFilters(),
      query: newQuery,
    });
  };

  const caseTypeOptions = caseTypes.map((type) => ({
    value: type.id.toString(),
    label: type.displayTitle || type.title,
  }));

  const tagOptions = caseTags.map((tag) => ({
    value: tag.id.toString(),
    label: isUnreadEmailTag(tag) ? t("case.unread_mail.tag") : tag.name,
    color: tag.color,
  }));

  const customDropdownFilters = [
    {
      key: "caseTypes",
      label: t("Case Types"),
      options: caseTypeOptions,
    },
    {
      key: "priorities",
      label: t("Priorities"),
      options: priorityOptions.map((priority, index) => ({
        value: (index + 1).toString(),
        label: priority.label,
      })),
    },
  ];

  const customCheckboxButtonFilters = [
    {
      key: "onlyOverdue",
      label: t("Only Overdue"),
      defaultValue: true,
    },
  ];

  const hasMore = (caseState.totalCount || 0) > (caseState.data?.length || 0);
  const listCount = caseState.totalCount != null ? `${caseState.totalCount}` : null;
  const moduleFilterSharedProps = {
    disabled: isLoading,
    onFiltersChange: handleFiltersChange,
    initialFilters: getInitialFilters(),
    acceptedStatuses: ["NEW", "PROCESSING", "FINISHED", "CLOSED"],
    userOptions,
    tagOptions,
    tagsFilterLabel: t("case_list.filter.tags"),
    customDropdownFilters,
    customCheckboxButtonFilters,
  };
  const secondaryFilterCount = (controller.filters || []).filter((filter) =>
    CASE_SECONDARY_FILTER_NAMES.includes(filter.name)
  ).length;
  const hasSecondaryFilters = secondaryFilterCount > 0;

  return (
    <>
      <DashboardHeader
        variant="list"
        chromeClassName="hidden xl:block"
        subtitle=""
        actions={[]}
        title={t("Cases")}
        listCount={listCount}
        meta={[]}
        mainButton={
          canCreateCase
            ? {
                label: t("New"),
                action: () => setCreateDialogOpen(true),
                path: null,
                color: "",
                icon: PlusIcon,
              }
            : undefined
        }
      >
        <ChecklistCompactPageHeader
          title={t("Cases")}
          progressLabel={listCount}
          actions={
            canCreateCase
              ? [
                  {
                    key: "new-case",
                    label: t("cases.create.dialog_title"),
                    icon: PlusIcon,
                    action: () => setCreateDialogOpen(true),
                    compactPrimary: true,
                  },
                ]
              : []
          }
          className="sticky top-0"
        />
        <div className="max-w-full space-y-2 overflow-x-hidden p-3 md:space-y-4 md:p-5">
          <div className="space-y-2 xl:hidden">
            <div className="flex items-center gap-2">
              <div className="relative flex h-11 min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200">
                <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <input
                  type="text"
                  value={controller.query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  placeholder={t("case_list.search_placeholder")}
                  className="min-w-0 flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
                {controller.query ? (
                  <button
                    type="button"
                    onClick={() => handleQueryChange("")}
                    className="grid h-11 w-11 flex-shrink-0 place-items-center text-slate-400 hover:text-slate-600"
                    aria-label={t("Clear")}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className={`relative inline-flex h-11 min-w-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors focus:outline-none ${
                  hasSecondaryFilters
                    ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                }`}
                aria-label={t("case_list.filter_title")}
              >
                <FunnelIcon className="h-5 w-5" />
                {hasSecondaryFilters ? (
                  <span className="min-w-[18px] rounded-full bg-white/20 px-1.5 text-center text-[11px] tabular-nums">
                    {secondaryFilterCount}
                  </span>
                ) : null}
              </button>
            </div>

            {!mobileFiltersOpen && hasSecondaryFilters ? (
              <ModuleFilter chipsOnly {...moduleFilterSharedProps} />
            ) : null}
          </div>

          <div className="hidden xl:block">
            <ModuleFilter
              filterCard
              {...moduleFilterSharedProps}
              searchPlaceholder={t("case_list.search_placeholder")}
              filterTitle={t("case_list.filter_title")}
              selectedItems={selectedItems}
            />
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)] md:rounded-[18px]">
            <div className="hidden md:block">
              <TableToolbar
                groupOptions={groupOptions}
                activeGroup={tableView.groupBy}
                onGroupChange={tableView.setGroupBy}
                columns={columnMeta}
                columnOrder={tableView.order}
                visibleKeys={tableView.visibleKeys}
                onToggleColumn={tableView.toggleColumn}
                onReorderColumn={tableView.reorderColumn}
                onResetColumns={tableView.resetColumns}
                savedViews={tableView.views}
                activeSavedViewId={tableView.activeViewId}
                savedViewDirty={tableView.dirty}
                onApplySavedView={tableView.applyView}
                onSaveAsView={tableView.saveAsView}
                onUpdateView={tableView.updateView}
                onDeleteView={tableView.deleteView}
                density={tableView.density}
                onDensityChange={tableView.setDensity}
              />
            </div>
            <CasesTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={caseState}
              density={tableView.density}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onItemsSelect={(items) => setSelectedItems(items)}
              onRowClick={(caseItem) => setDrawerCase(caseItem)}
              layout="responsive"
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Floating bulk action bar */}
      <BulkBar
        count={selectedItems.length}
        onClear={() => setSelectedItems([])}
        actions={
          canEditCase
            ? [
          {
            label: t("Edit"),
            icon: PencilSquareIcon,
            action: () => toast.error(t("Kommer snart")),
          },
          { separator: true },
          {
            label: t("Delete"),
            icon: TrashIcon,
            danger: true,
            action: () => toast.error(t("Kommer snart")),
          },
            ]
            : []
        }
      />

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerCase}
        onClose={() => setDrawerCase(null)}
        fullBleedMobile
        eyebrow={drawerCase ? `${t("Case")} ${drawerCase.case_number || ""}` : ""}
        title={
          drawerCase
            ? caseCustomerName(drawerCase) || drawerCase.title || t("Cases")
            : t("Cases")
        }
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/cases/${drawerCase.id}`),
          },
        ]}
      >
        {drawerCase && (
          <CaseDetailDrawer
            caseItem={drawerCase}
            userOptions={userOptions}
            onUpdate={handleCaseUpdate}
            onUnreadMailCleared={(updated) => {
              if (!updated?.id) return;
              setDrawerCase((prev) =>
                prev && String(prev.id) === String(updated.id)
                  ? { ...prev, ...updated, tag_definitions: updated.tag_definitions }
                  : prev
              );
            }}
          />
        )}
      </RightDrawer>

      <CaseCreateOnboardingDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => dispatch(getCasesAsync(controller))}
      />

      <PMDialog
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title={t("case_list.filter_title")}
        size="md"
        dense
        closeOnOutsideClick
      >
        <div className="overflow-y-auto overscroll-contain px-3 pb-3 sm:px-4 sm:pb-4">
          <ModuleFilter
            panelMode
            hideSearch
            {...moduleFilterSharedProps}
          />
        </div>
      </PMDialog>
    </>
  );
};
